import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Badge, Spinner, Modal } from '../../components/UI';
import { attendanceAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  HiOutlineClock,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineDownload,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineArrowRight
} from 'react-icons/hi';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';

const COLORS = {
  active: '#E7000B',
  completed: '#10B981',
  notCheckedIn: '#F59E0B'
};

const StatCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    primary: 'bg-primary-600',
    success: 'bg-green-500',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500'
  };

  return (
    <Card className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color] || 'bg-primary-600'}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </Card>
  );
};

const Attendance = () => {
  const [filterMode, setFilterMode] = useState('day'); // 'day' or 'month'
  const [dateFilter, setDateFilter] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [monthFilter, setMonthFilter] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [userFilter, setUserFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate date range based on filter mode
  const getDateRange = () => {
    if (filterMode === 'day') {
      return { startDate: dateFilter, endDate: dateFilter };
    } else {
      // Month mode - calculate first and last day of month
      const [year, month] = monthFilter.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of month
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    }
  };

  const dateRange = getDateRange();

  // Fetch users for filter
  const { data: usersData } = useQuery({
    queryKey: ['usersForFilter'],
    queryFn: async () => {
      const response = await usersAPI.getAll({ limit: 100 });
      return response.data?.data || [];
    },
  });

  // Fetch attendance records
  const { data: attendanceData, isLoading, refetch } = useQuery({
    queryKey: ['attendance', filterMode, filterMode === 'day' ? dateFilter : monthFilter, userFilter, page],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (filterMode === 'day') {
        params.date = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      if (userFilter) params.userId = userFilter;
      const response = await attendanceAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['attendanceStats', filterMode, filterMode === 'day' ? dateFilter : monthFilter],
    queryFn: async () => {
      const params = {};
      if (filterMode === 'day') {
        params.date = dateFilter;
      } else {
        params.startDate = dateRange.startDate;
        params.endDate = dateRange.endDate;
      }
      const response = await attendanceAPI.getStats(params);
      return response.data?.data || {};
    },
  });

  const records = attendanceData?.data || [];
  const pagination = attendanceData?.pagination || {};
  const users = usersData || [];

  // Pie chart data
  const pieData = [
    { name: 'Active Sessions', value: stats?.activeSessions || 0, color: COLORS.active },
    { name: 'Completed Sessions', value: stats?.completedSessions || 0, color: COLORS.completed },
  ].filter(item => item.value > 0);

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    return format(new Date(timeStr), 'hh:mm a');
  };

  const formatDuration = (hours) => {
    if (!hours) return '0 H 0 M';
    // Convert hours to total minutes (backend sends hours as decimal, e.g., 0.85 hours = 51 minutes)
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h} H ${m} M`;
  };

  const handleRowClick = (record) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleExport = async () => {
    try {
      toast.loading('Generating report...');
      const params = { startDate: dateRange.startDate, endDate: dateRange.endDate };
      if (userFilter) params.userId = userFilter;

      const response = await attendanceAPI.exportReport(params);
      toast.dismiss();

      if (response.data?.success && response.data?.data) {
        const records = response.data.data;
        if (records.length === 0) {
          toast.error('No data to export');
          return;
        }

        // Build well-structured CSV - backend already flattens sessions
        const csvRows = [];

        // Header
        csvRows.push('=== ATTENDANCE EXPORT ===');
        csvRows.push('');
        csvRows.push(`Export Date: ${new Date().toLocaleString()}`);
        csvRows.push(`Date Range: ${filterMode === 'day' ? dateFilter : `${monthFilter}`}`);
        csvRows.push(`Total Sessions: ${records.length}`);
        csvRows.push('');

        // Column headers - matching backend response
        csvRows.push([
          'Date',
          'Employee Name',
          'Email',
          'Department',
          'Session #',
          'Check In Time',
          'Check Out Time',
          'Working Hours',
          'Status'
        ].join(','));

        // Data rows - backend returns flattened records
        records.forEach(record => {
          csvRows.push([
            `"${record.Date || ''}"`,
            `"${record['Employee Name'] || ''}"`,
            `"${record.Email || ''}"`,
            `"${record.Department || ''}"`,
            `"${record['Session #'] || 1}"`,
            `"${record['Check In'] || ''}"`,
            `"${record['Check Out'] || ''}"`,
            `"${record['Working Hours'] || 'N/A'}"`,
            `"${record.Status || ''}"`
          ].join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = filterMode === 'day' ? `attendance-${dateFilter}.csv` : `attendance-${monthFilter}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded successfully');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
      console.error('Export error:', error);
    }
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Attendance Management</h1>
            <p className="text-gray-500 text-sm mt-0.5 sm:mt-1">Track telecaller check-in/out sessions</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineRefresh className="w-4 h-4" />}
              onClick={() => refetch()}
            >
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExport}
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            title="Total Telecallers"
            value={stats?.totalRecords || 0}
            icon={<HiOutlineUser className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="primary"
          />
          <StatCard
            title="Currently Active"
            value={stats?.activeUsers || 0}
            icon={<HiOutlineCheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="success"
          />
          <StatCard
            title="Total Sessions"
            value={stats?.totalSessions || 0}
            icon={<HiOutlineClock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="info"
          />
          <StatCard
            title="Total Hours"
            value={stats?.totalWorkingHoursFormatted || '0h 0m'}
            icon={<HiOutlineCalendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
            color="warning"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {/* Pie Chart */}
          <Card padding="p-3 sm:p-4 lg:p-6" className="lg:col-span-1">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Session Status</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={160} className="sm:h-[200px]">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-40 sm:h-48 text-gray-400 text-sm">
                No data available
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mt-3 sm:mt-4">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Active Telecallers List */}
          <Card padding="p-3 sm:p-4 lg:p-6" className="lg:col-span-2">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Currently Active Telecallers</h3>
            {stats?.activeTelecallers?.length > 0 ? (
              <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                {stats.activeTelecallers.map((telecaller) => (
                  <div key={telecaller.userId} className="flex items-center justify-between p-2.5 sm:p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-green-700 font-medium text-sm">
                          {telecaller.name?.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{telecaller.name}</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{telecaller.department}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <Badge variant="success" size="sm">Active</Badge>
                      <p className="text-xs text-gray-500 mt-0.5 sm:mt-1">
                        Since {formatTime(telecaller.checkInTime)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 sm:h-48 text-gray-400 text-sm">
                No telecallers currently active
              </div>
            )}
          </Card>
        </div>

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Filter Mode Toggle */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">View By</label>
              <div className="flex rounded-lg border-2 border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setFilterMode('day'); setPage(1); }}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                    filterMode === 'day'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Day
                </button>
                <button
                  type="button"
                  onClick={() => { setFilterMode('month'); setPage(1); }}
                  className={`px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                    filterMode === 'month'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Month
                </button>
              </div>
            </div>
            {/* Date/Month Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {filterMode === 'day' ? 'Date' : 'Month'}
              </label>
              {filterMode === 'day' ? (
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none text-sm"
                />
              ) : (
                <input
                  type="month"
                  value={monthFilter}
                  onChange={(e) => { setMonthFilter(e.target.value); setPage(1); }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none text-sm"
                />
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Telecaller</label>
              <select
                value={userFilter}
                onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none bg-white text-sm"
              >
                <option value="">All Telecallers</option>
                {users.filter(u => u.role === 'telecaller').map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Attendance Table */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : records.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center py-8 sm:py-12">
              <HiOutlineClock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900">No attendance records</h3>
              <p className="text-gray-500 text-sm mt-1 text-center px-4">Attendance records for the selected date will appear here</p>
            </div>
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Telecaller</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Sessions</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Total Hours</th>
                    <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((record) => {
                    const sessions = record.sessions || [];
                    const isActive = sessions.some(s => s.status === 'active');

                    return (
                      <tr
                        key={record._id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(record)}
                      >
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <div className="flex items-center gap-2 lg:gap-3">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {record.userId?.firstName?.charAt(0)}{record.userId?.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {record.userId?.firstName} {record.userId?.lastName}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-500">{record.userId?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4 text-gray-600 text-sm">
                          {format(new Date(record.date), 'MMM d, yyyy')}
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <span className="font-medium text-sm">{sessions.length}</span>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <span className="font-medium text-primary-600 text-sm">
                            {formatDuration(record.totalWorkingHours || 0)}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-3 lg:py-4">
                          <Badge variant={isActive ? 'success' : 'default'} size="sm">
                            {isActive ? 'Active' : 'Completed'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {records.map((record) => {
                const sessions = record.sessions || [];
                const isActive = sessions.some(s => s.status === 'active');

                return (
                  <div
                    key={record._id}
                    className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(record)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-medium text-sm">
                            {record.userId?.firstName?.charAt(0)}{record.userId?.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {record.userId?.firstName} {record.userId?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(record.date), 'MMM d, yyyy')} • {sessions.length} sessions
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="font-medium text-primary-600 text-sm">
                          {formatDuration(record.totalWorkingHours || 0)}
                        </span>
                        <Badge variant={isActive ? 'success' : 'default'} size="sm">
                          {isActive ? 'Active' : 'Done'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Session Details Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={selectedRecord ? `${selectedRecord.userId?.firstName} ${selectedRecord.userId?.lastName} - Session Details` : 'Session Details'}
          size="lg"
        >
          {selectedRecord && (
            <div className="space-y-4 sm:space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Date</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {format(new Date(selectedRecord.date), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="bg-primary-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-primary-600">Total Hours</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary-700">
                    {formatDuration(selectedRecord.totalWorkingHours || 0)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 text-center">
                  <p className="text-xs sm:text-sm text-gray-500">Total Sessions</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {selectedRecord.sessions?.length || 0}
                  </p>
                </div>
              </div>

              {/* Sessions List */}
              <div>
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 sm:mb-4">Check-in / Check-out Times</h4>
                <div className="space-y-2 sm:space-y-3">
                  {selectedRecord.sessions?.map((session, index) => {
                    const isActive = session.status === 'active';
                    const sessionMinutes = session.workingHours || 0;

                    return (
                      <div
                        key={session._id || index}
                        className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 ${
                          isActive
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                          <span className="font-medium text-gray-900 text-sm">Session {index + 1}</span>
                          <Badge variant={isActive ? 'success' : 'default'} size="sm">
                            {isActive ? 'Active' : 'Completed'}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                          {/* Check In */}
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Check In</p>
                            <p className="text-sm sm:text-lg font-semibold text-gray-900">
                              {formatTime(session.checkIn?.time)}
                            </p>
                          </div>

                          {/* Arrow */}
                          <div className="flex items-center justify-center">
                            <HiOutlineArrowRight className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400" />
                          </div>

                          {/* Check Out */}
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Check Out</p>
                            <p className={`text-sm sm:text-lg font-semibold ${isActive ? 'text-green-600' : 'text-gray-900'}`}>
                              {session.checkOut?.time ? formatTime(session.checkOut.time) : 'Active'}
                            </p>
                          </div>

                          {/* Duration */}
                          <div className="flex-1 text-right">
                            <p className="text-xs text-gray-500 mb-0.5 sm:mb-1">Duration</p>
                            <p className="text-sm sm:text-lg font-semibold text-primary-600">
                              {formatDuration(sessionMinutes)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
                <Button variant="outline" onClick={handleCloseModal}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default Attendance;